// =============================================================================
// src/views/mapa-eleitoral/styles.ts — Estilos de choropleth da Tela 01.
//
// Proveniencia: assets/unified-app.js Sections.mapaEleitoral._applyStyles
//   (linhas 877-898). Constroi as funcoes de estilo de zona e municipio e as
//   injeta no MapController via setZoneStyles/setMunStyles. Cores e opacidades
//   (0.72 / 0.55 / weights / rgba) replicadas FIEL — sao paridade visual.
//
// SRP: so a logica de cor/estilo. Sem efeito de DOM, sem timers.
// =============================================================================

import type {
  MapController,
  ZonaKey,
  MunicipalityId,
  ElectoralYear,
  LegendLayerKey,
  ThematicLayerKey,
  HexColor,
} from '../../app/types';
import type { PathOptions } from 'leaflet';
import {
  MANAUS_ELECTORAL_BY_YEAR,
  MANAUS_LAYER_COLORS,
  MANAUS_MUN_LAYER_PALETTES,
  getMunData,
  simpleHashOf,
} from '../../data';

/** True quando a camada e a base partidaria — narrowing para as camadas tematicas. */
function isPartidoLayer(layer: LegendLayerKey): layer is 'partido' {
  return layer === 'partido';
}

/** Cor de preenchimento de uma zona (ramo partido x tematica do _applyStyles). */
function zoneFillColorOf(layer: LegendLayerKey, year: ElectoralYear, zonaKey: ZonaKey): HexColor {
  if (isPartidoLayer(layer)) {
    // electoral[year][zona][1] = cor do partido vencedor (tupla ZoneElectoralResult).
    return MANAUS_ELECTORAL_BY_YEAR[year][zonaKey][1];
  }
  const thematicLayer: ThematicLayerKey = layer;
  return MANAUS_LAYER_COLORS[thematicLayer][zonaKey];
}

/** Cor de preenchimento de um municipio (ramo partido x hash-na-rampa do _applyStyles). */
function munFillColorOf(layer: LegendLayerKey, year: ElectoralYear, munId: MunicipalityId): HexColor {
  if (isPartidoLayer(layer)) {
    return getMunData(munId, year)[1];
  }
  const ramp = MANAUS_MUN_LAYER_PALETTES[layer];
  // Distribuicao deterministica na rampa: hash(id+camada) % len (original preservado).
  const rampColor = ramp[simpleHashOf(munId + layer) % ramp.length];
  /* v8 ignore next 5 -- rampa nunca vazia (MANAUS_MUN_LAYER_PALETTES sempre >=1 cor); guarda noUncheckedIndexedAccess CLAUDE.md rule 11 */
  if (rampColor === undefined) {
    throw new Error(
      `munFillColorOf: rampa vazia para camada ${JSON.stringify(layer)}, esperado >= 1 cor`,
    );
  }
  return rampColor;
}

/**
 * Aplica as funcoes de estilo de zona e municipio no mapa (replica _applyStyles).
 * Le o estado atual (camada/ano) via getters para que o MapController re-renderize
 * com as cores certas. Opacidades/weights mantidos identicos ao original.
 */
export function applyMapaEleitoralStyles(
  mapCtrl: MapController,
  getCurrentLayer: () => LegendLayerKey,
  getCurrentYear: () => ElectoralYear,
): void {
  mapCtrl.setZoneStyles((zonaKey): PathOptions => {
    const fillColor = zoneFillColorOf(getCurrentLayer(), getCurrentYear(), zonaKey);
    return { fillColor, fillOpacity: 0.72, weight: 0.8, color: 'rgba(255,255,255,0.6)', opacity: 1 };
  });
  mapCtrl.setMunStyles((munId): PathOptions => {
    const fillColor = munFillColorOf(getCurrentLayer(), getCurrentYear(), munId);
    return { fillColor, fillOpacity: 0.55, weight: 1, color: '#ffffff', opacity: 0.7 };
  });
}
